import { cloneDeep, isUndefined, find, isEmpty, compact, isObject } from 'lodash';
import { BaseService } from '@/modules/core/app/services/abstracts/BaseService';
import WidgetResource from '@/modules/ta/widget/resources/WidgetResource';
import { WidgetType } from '@/modules/ta/widget/models/WidgetType';
import WidgetContextFactory from '@/modules/ta/widget/models/context/WidgetContextFactory';
import {
  WidgetBuilderConstants,
  WidgetType as WidgetTypeConstant,
  WidgetTypeGrouping,
  WidgetLogMessage,
  WidgetReportDisplayLength,
} from '@/modules/ta/widget/widget.constants';
import { WidgetContextService } from '@/modules/ta/widget/services/WidgetContextService';
import { DataSource } from '@/modules/core/datasource/models/DataSource';
import { getters } from '@/modules/core/app/helpers/store';
import { AppModule } from '@/modules/core/app/constants/app.constants';

export class WidgetService extends BaseService {
  constructor() {
    super();
    this.resource = WidgetResource;
  }

  /**
   * @param id
   * @param params
   * @returns {Promise<*>}
   */
  async getItem(id, params = {}) {
    return this.resource.getItem(id, { ...params, all: true });
  }

  /**
   * @param {Widget} widget
   * @returns {*}
   */
  async copyItem(widget) {
    return this.resource.copyItem(widget.id);
  }

  async resize(widget) {
    return this.resource.resize(widget);
  }

  /**
   * @returns {WidgetType[]}
   */
  async getWidgetDataTypes(all) {
    const types = [
      WidgetType.getBigNumberType(),
      WidgetType.getDataGridType(),
      WidgetType.getBarChartType(),
      WidgetType.getLineChartType(),
      WidgetType.getCombinationChartType(),
      WidgetType.getBubbleChartType(),
      WidgetType.getPieChartType(),
      WidgetType.getFunnelChartType(),
      WidgetType.getGaugeChartType(),
      WidgetType.getGeoChartType(),
      WidgetType.getSankeyChartType(),
      WidgetType.getPivotTableType(),
    ];
    if (all) {
      types.unshift(WidgetType.getAccountManagerType());
      types.push(WidgetType.getMediaType());
    }
    return types;
  }

  /**
   * @param type
   * @returns {WidgetType}
   */
  getWidgetTypeByType(type) {
    switch (type) {
      case WidgetTypeConstant.TYPE_BIGNUMBER:
        return WidgetType.getBigNumberType();
      case WidgetTypeConstant.TYPE_DATAGRID:
        return WidgetType.getDataGridType();
      case WidgetTypeConstant.TYPE_BARCHART:
        return WidgetType.getBarChartType();
      case WidgetTypeConstant.TYPE_LINECHART:
        return WidgetType.getLineChartType();
      case WidgetTypeConstant.TYPE_COMBINATIONCHART:
        return WidgetType.getCombinationChartType();
      case WidgetTypeConstant.TYPE_BUBBLECHART:
        return WidgetType.getBubbleChartType();
      case WidgetTypeConstant.TYPE_PIECHART:
        return WidgetType.getPieChartType();
      case WidgetTypeConstant.TYPE_FUNNELCHART:
        return WidgetType.getFunnelChartType();
      case WidgetTypeConstant.TYPE_GAUGECHART:
        return WidgetType.getGaugeChartType();
      case WidgetTypeConstant.TYPE_GEOCHART:
        return WidgetType.getGeoChartType();
      case WidgetTypeConstant.TYPE_ACCOUNTMANAGER:
        return WidgetType.getAccountManagerType();
      case WidgetTypeConstant.TYPE_MEDIA:
        return WidgetType.getMediaType();
      case WidgetTypeConstant.TYPE_CHATGPT:
        return WidgetType.getChatGPTType();
      case WidgetTypeConstant.TYPE_EXECUTIVESUMMARY:
        return WidgetType.getExecutiveSummaryType();
      case WidgetTypeConstant.TYPE_SANKEYCHART:
        return WidgetType.getSankeyChartType();
      case WidgetTypeConstant.TYPE_PIVOTTABLE:
        return WidgetType.getPivotTableType();
      default:
        Logger.log(WidgetLogMessage.UNSUPPORTED_TYPE, Logger.LEVEL_WARNING);
    }
  }

  /**
   * @param type
   * @returns {string}
   */
  getWidgetIcon(type) {
    return this.getWidgetTypeByType(type)?.image;
  }

  /**
   * @param {Widget} widget
   * @returns {boolean}
   */
  isWidgetModuleDisabled(widget) {
    const userSettings = getters.session.getUserSettings();
    if (!userSettings || !widget) {
      return false;
    }
    return (
      (widget.isGoalWidget() && !userSettings.isModuleAvailable(AppModule.GOALS)) ||
      (widget.isLeadsWidget() && !userSettings.isModuleAvailable(AppModule.MANAGE_LEADS))
    );
  }

  getWidgetTypeGrouping(widgetType) {
    switch (widgetType) {
      case WidgetTypeConstant.TYPE_MEDIA:
        return WidgetTypeGrouping.DISPLAY;
      case WidgetTypeConstant.TYPE_ACCOUNTMANAGER:
        return WidgetTypeGrouping.ADMIN;
      case WidgetTypeConstant.TYPE_CHATGPT:
        return WidgetTypeGrouping.CHATGPT;
      case WidgetTypeConstant.TYPE_EXECUTIVESUMMARY:
        return WidgetTypeGrouping.EXECUTIVESUMMARY;
      default:
        return WidgetTypeGrouping.DATASOURCED;
    }
  }

  /**
   * @param {Widget} widget
   * @returns {Promise<Widget>}
   */
  async saveItem(widget) {
    const widgetId = widget.id === WidgetBuilderConstants.NEW_WIDGET_ID ? null : widget.id;
    const model = this.trimWidget(widget);
    const id = await this.resource.saveItem(model, widgetId);
    return WidgetResource.getItem(id, { all: 1 });
  }

  /**
   * @param {Widget} widget
   * @returns {Promise<Widget>}
   */
  async saveBuilder({ widget, extra = null }) {
    const widgetId = widget.id === WidgetBuilderConstants.NEW_WIDGET_ID ? null : widget.id;
    const model = this.trimWidget(widget);
    const id = await this.resource.saveBuilder({ model, extra }, widgetId);
    const result = this.resource.getItem(id, { all: 1 });
    return result.then((fetchedItem) => {
      if (WidgetContextService.isReportStudioContext() && widget.isMediaWidget()) {
        fetchedItem.metadata.content = widget.metadata.content;
      }
      return fetchedItem;
    });
  }

  /**
   * @param {Widget} widget
   * @returns {Promise<*>}
   */
  async deleteWidget(widget) {
    return this.resource.deleteItem(widget.id);
  }

  /**
   * @param widget
   * @returns {*}
   */
  static getChartPalette(widget) {
    return widget.metadata.chart_palette?.length
      ? widget.metadata.chart_palette
      : WidgetContextFactory.getContext().getChartPalette(widget);
  }

  /**
   * Trims down widget to its database structure
   * @param widget
   */
  trimWidget(widget) {
    // Create copy of model to avoid breaking widget caused by changes below and mutating store data
    const payload = cloneDeep(widget);
    payload.invalidateFilters();
    return this.trimWidgetMetadata(payload);
  }

  /**
   * @param {string} type
   * @returns {*}
   */
  getMetadataColumnValues(type) {
    return this.resource.getMetadataColumnValues(type, {
      fields: 'data_source',
    });
  }

  /**
   * Trims down widget metadata to its database structure
   *
   * @param {Widget} payload
   *
   * @returns {Widget} // backend structure (what is stored in the DB)
   */
  trimWidgetMetadata(payload) {
    // Never send back full column metadata, only keep field names
    if (!isUndefined(payload.metadata.data_columns)) {
      let selectedColumns = payload.metadata.data_columns.selected;
      const groupedColumns = payload.metadata.data_columns.grouped;
      const benchmarkColumns = payload.metadata.data_columns.benchmarks ?? [];
      const instanceBenchmarkColumns = payload.metadata.data_columns.instance_benchmarks ?? [];

      if (!isUndefined(payload.metadata.time_grouping)) {
        // Only set time grouping if grouped field is primary date field, ALL other cases set to null.
        const primaryDateColumn = find(groupedColumns, { is_primary_date_field: true });
        if (isEmpty(groupedColumns) || isEmpty(primaryDateColumn)) {
          payload.metadata.time_grouping = null;
        }
      }

      if (payload.type === WidgetTypeConstant.TYPE_GAUGECHART && selectedColumns.length > 2) {
        selectedColumns = selectedColumns.slice(0, 2);
      }

      payload.metadata.data_columns.selected = selectedColumns.map((column) => column.field);
      payload.metadata.data_columns.grouped = groupedColumns.map((column) => column.field);
      payload.metadata.data_columns.benchmarks = benchmarkColumns.map((column) => column.field);
      payload.metadata.data_columns.instance_benchmarks = instanceBenchmarkColumns.map(
        (column) => column.field
      );

      if (payload.metadata.data_columns.benchmarks.length) {
        payload.metadata.benchmark = payload.metadata.data_columns.benchmarks;
      }
      if (payload.metadata.data_columns.instance_benchmarks.length) {
        payload.metadata.instance_benchmark = payload.metadata.data_columns.instance_benchmarks;
      }
    }

    // Never send dynamic metadata
    delete payload.metadata.dynamic;
    if (payload.type === WidgetTypeConstant.TYPE_DATAGRID) {
      const { metadata } = payload;
      payload.metadata.user_grouped_columns = this.transformColumns(metadata.user_grouped_columns);
      payload.metadata.user_conditional_columns = this.transformColumns(
        metadata.user_conditional_columns
      );
      if (
        payload.metadata?.draw_options?.report_display_length === WidgetReportDisplayLength.DEFAULT
      ) {
        payload.metadata.draw_options.report_display_length = null;
      }
    }
    return payload;
  }

  transformColumns(columns) {
    if (!columns) return [];
    return (
      columns
        .map((grouped) => {
          // If 'grouped.values' is an array of objects, transform it to an array of 'field'
          if (grouped?.values?.map) {
            delete grouped.options; // Remove 'options'
            // Check if values are already strings (from previous save) or objects
            grouped.values = compact(grouped.values).map((col) =>
              isObject(col) ? col.field : col
            );
          }
          return grouped;
        })
        // Filter out columns with empty 'values'
        .filter((grouped) => grouped?.values?.length !== 0)
    );
  }

  async getItems(params) {
    return this.resource.getItems(params);
  }

  async getDataComparisonValues() {
    return this.resource.getComparisonValues();
  }

  async getDataSparklineValues() {
    return this.resource.getSparklineValues();
  }

  // Static promise cache for Mapbox token
  static _mapboxTokenPromise = null;

  async getMapboxAccessToken() {
    // Return existing promise if a request is in progress
    if (WidgetService._mapboxTokenPromise) {
      return WidgetService._mapboxTokenPromise;
    }

    // Create new promise and cache it
    WidgetService._mapboxTokenPromise = (async () => {
      try {
        const result = await this.resource.getMapboxAccessToken();
        return result;
      } finally {
        // Clear promise cache after completion (success or failure)
        setTimeout(() => {
          WidgetService._mapboxTokenPromise = null;
        }, 0);
      }
    })();

    return WidgetService._mapboxTokenPromise;
  }

  /**
   * @param widget
   * @returns {Promise<*>}
   */
  async deleteWidgetFilterSet(widget) {
    return this.resource.deleteWidgetFilterSet(widget.id);
  }

  /**
   * @returns {Promise<*>}
   */
  async getMapBoxLayers() {
    return this.resource.getMapBoxLayers();
  }

  async getLibraryDataSources(params = {}) {
    const result = await this.resource.getLibraryDataSources(params);
    return Object.values(result).map((item) => new DataSource(item));
  }

  /**
   *
   * @param widget
   * @param extra
   * @returns {Promise<*>}
   */
  async saveWidgetUsingBuilder({ widget, extra = null }) {
    const widgetId = widget.id === WidgetBuilderConstants.NEW_WIDGET_ID ? null : widget.id;
    const model = this.trimWidget(widget);
    return this.resource.saveBuilder({ model, extra }, widgetId);
  }
}

export default new WidgetService();
